import React, {
    Component,
    MouseEvent
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import shallowEqual from 'shallowequal';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import parse from 'html-react-parser';

import {Button} from 'primereact/button';
import {
    Column,
    ColumnBodyOptions,
    ColumnEditorOptions,
    ColumnEvent
} from 'primereact/column';
import {ContextMenu} from 'primereact/contextmenu';
import {
    DataTable,
    DataTableRowEvent,
    DataTableContextMenuSingleSelectionChangeEvent,
    DataTableSortMeta
} from 'primereact/datatable';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';
import {MenuItem} from 'primereact/menuitem';
import {Tooltip} from 'primereact/tooltip';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import PropertySet, {PropertyValueType} from '~/shared/src/mm/PropertySet';
import Item, {ItemAttributesSettable} from '~/shared/src/ci/Item';
import Property, {PropertyAttributesSettable} from '~/shared/src/ci/Property';
import {
    valuePrecision,
    getTargetArea
} from '~/shared/src/utilities/modules/finder';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';
import {validateValueForType} from '~/shared/src/utilities/valueValidators';
import {timestampRenderer} from '~/shared/src/utilities/valueRenderers';

import StatusDropdown from '~/frontend/src/components/base/shared/field/StatusDropdown';

import {StoreState} from '~/frontend/src/store';
import {
    getPropertySetDisplayName,
    getValueDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {
    renderValue,
    statusColoringRenderer,
    statusRenderer
} from '~/frontend/src/utilities/valueRenderers';
import {
    getValueEditor,
    getValueSetEditor
} from '~/frontend/src/utilities/valueEditors';


import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as DataActions from '~/frontend/src/actions/data';
import {UpdateItem} from '~/frontend/src/actions/data/updateItem';


const Actions = Object.assign( {}, PanelActions, DataActions ) as any;
type SetParam = <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof AllProps ) => UnsetParamAction;


type DataTableState = { [ key : string ] : any };

const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show empty (not set) properties.
     */
    showEmptyProperties: true as boolean,

    /**
     * Show default values for properties.
     */
    showPropertyDefaults: true as boolean,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean,

    /**
     * The state for the DataTable
     */
    dataTableState: {
        multiSortMeta: [ { field: 'propertyTypeName', order: 1 } ]
    } as DataTableState
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The itemId the properties belongs to.
     */
    itemId : number;

    /**
     * The itemTypeId of the item the properties belongs to.
     */
    itemTypeId : number;

    /**
     * The statusId of the item the properties belongs to.
     */
    itemStatusId : number;

    /**
     * The properties.
     *
     * Will be mapped from global state.
     */
    properties : Item[ 'properties' ];

    /**
     * Parameters for deep linking of a property
     */
    deepLinkProps? : {
        property : string;
    };
};

type StateProps = {
    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // common panel actions
    setParam : SetParam;
    unsetParam : UnsetParam;

    // data actions
    updateItem : UpdateItem;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    schema? : Schema;
    timezone? : string;
    itemId? : number;
    itemTypeId? : number;
    properties? : Item[ 'properties' ];
    rawMode? : boolean;
    showAdditionalRawNames? : boolean;
    showEmptyProperties? : boolean;
    showPropertyDefaults? : boolean;
    user? : User;
    //
    propertySets : PropertySet[];
    insertablePropertySets : PropertySet[];
    data : RowData[];
    multiSortMeta? : DataTableSortMeta[];
    contextMenuSelection? : any;
    contextMenuCellInfo? : {
        columnField : string;
        rowData : RowData;
    };
};
const stateKeys = keys<State>();

type RowData = {
    propertySet? : PropertySet;
    propertyTypeName? : string;
    editPropertyTypeName? : string;
    valueType? : PropertyValueType | 'VALUESET';
    valueDefault? : string | JSX.Element | ( string | JSX.Element )[];
    property? : Property;
    id? : number;
    value? : string | JSX.Element | ( string | JSX.Element )[];
    editValue? : AttributeValue;
    status? : string;
    editStatus? : Status;
    modifierUserId? : string;
    modificationTime? : NDate;
    validatorUserId? : string;
    validationTime? : NDate;
    validFromTime? : NDate;
    validToTime? : NDate;
    error? : string;
}

const activeStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'active' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'active' )?.id
};

const historyStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'history' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'history' )?.id
};

const deletedStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'deleted' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'deleted' )?.id
};

export const resetStatusIdCaches = () => {
    activeStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'active' )?.id;
    activeStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'active' )?.id;
    historyStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'history' )?.id;
    historyStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'history' )?.id;
    deletedStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id;
    deletedStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'deleted' )?.id;
};

/**
 * PropertiesDataTable
 */
class PropertiesDataTable extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /* properties *************************************************************************************************************************/

    contextMenu? : ContextMenu | null;
    dataTable? : DataTable<RowData[]> | null;
    lastDataTableState = {};
    ignoreDataTableSaveState = false as boolean;

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            propertySets          : [],
            insertablePropertySets: [],
            data                  : []
        };

        this.getColumns = memoize( this.getColumns );
        this.getContextMenuModel = memoize( this.getContextMenuModel );

        this.onAddRowButtonClick = this.onAddRowButtonClick.bind( this );
        this.onResolveRowClass = this.onResolveRowClass.bind( this );
        this.onBeforeCellEditShow = this.onBeforeCellEditShow.bind( this );
        this.onCellEditCancel = this.onCellEditCancel.bind( this );

        // TODO: find a proper solution for that. Problem is, that onCellEditComplete fires before onChange event of Combo
        const onCellEditCompleteDeferred = this.onCellEditComplete;
        this.onCellEditComplete = ( params : ColumnEvent ) => {
            setTimeout( () => {
                onCellEditCompleteDeferred.call( this, params );
            }, 10 );
        }
        this.onValueEditorResize = this.onValueEditorResize.bind( this );
        this.onCustomSaveState = this.onCustomSaveState.bind( this );
        this.onCustomRestoreState = this.onCustomRestoreState.bind( this );
        this.onContextMenuSelectionChange = this.onContextMenuSelectionChange.bind( this );
        this.onContextMenu = this.onContextMenu.bind( this );
        this.onContextMenuHide = this.onContextMenuHide.bind( this );

        this.columnActionRenderer = this.columnActionRenderer.bind( this );
        this.columnBodyRenderer = this.columnBodyRenderer.bind( this );

        this.getPropertyTypeEditor = this.getPropertyTypeEditor.bind( this );
        this.getValueEditor = this.getValueEditor.bind( this );
        this.getStatusEditor = this.getStatusEditor.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) {
        const retVal : Partial<State>                                                          = {},
              changedProps : ( Extract<keyof AllProps, keyof State> )[]                        = stateKeys.reduce(
                  ( innerRetVal : string[], prop ) => {
                      if (
                          props[ prop as keyof AllProps ] !== undefined
                          && props[ prop as keyof AllProps ] !== state[ prop ]
                      ) {
                          innerRetVal.push( prop as Extract<keyof AllProps, keyof State> );
                      }

                      return innerRetVal;
                  },
                  []
              ) as ( Extract<keyof AllProps, keyof State> )[],
              propertySetsChangingProps : ( Extract<keyof AllProps, keyof State> )[]           = [
                  'schema',
                  'itemTypeId',
                  'user'
              ],
              computePropertySets                                                              = !state.data.length || changedProps.some( prop => propertySetsChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) ),
              insertablePropertySetsChangingProps : ( Extract<keyof AllProps, keyof State> )[] = [
                  'schema',
                  'itemTypeId',
                  'properties',
                  'user'
              ],
              computeInsertablePropertySets                                                    = !state.data.length || changedProps.some( prop => insertablePropertySetsChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) ),
              dataChangingProps : ( Extract<keyof AllProps, keyof State> )[]                   = [
                  'schema',
                  'timezone',
                  'properties',
                  'rawMode',
                  'showAdditionalRawNames',
                  'showEmptyProperties',
                  'showPropertyDefaults',
                  'user'
              ],
              computeData                                                                      = !state.data.length || changedProps.some( prop => dataChangingProps.includes( prop as Extract<keyof AllProps, keyof State> ) );

        // compute PropertySets?
        if ( computePropertySets ) {
            retVal.propertySets = PropertiesDataTable.getPropertySets( props );
        }

        // compute insertable PropertyTypes?
        if ( computeInsertablePropertySets ) {
            retVal.insertablePropertySets = PropertiesDataTable.getInsertablePropertySets( props );
        }

        // compute data?
        if ( computeData ) {
            const propertySets = retVal.propertySets ?? state.propertySets;

            retVal.data = PropertiesDataTable.getData( props, propertySets );
        }

        // sort data?
        if (
            props.dataTableState.multiSortMeta?.length
            && (
                computeData
                || !shallowEqual( props.dataTableState.multiSortMeta, state.multiSortMeta )
            )
        ) {
            const data = retVal.data ?? ( [] as RowData[] ).concat( state.data );

            retVal.data = PropertiesDataTable.sortData( data, props.dataTableState.multiSortMeta );
        }

        // update state
        for ( const prop of changedProps ) {
            retVal[ prop as keyof State ] = props[ prop ] as State[ typeof prop ];
        }
        if ( props.dataTableState.multiSortMeta !== state.multiSortMeta ) {
            retVal.multiSortMeta = props.dataTableState.multiSortMeta;
        }

        return retVal;
    }

    /**
     * Gets the PropertySets.
     *
     * @param props
     */
    static getPropertySets( props : AllProps ) : PropertySet[] {
        const retVal : PropertySet[] = [],
              {
                  schema,
                  itemTypeId,
                  user
              }                      = props,
              propertySets           = metaModel[ schema ].getPropertySetsForItemType( itemTypeId );

        for ( const propertySet of propertySets ) {
            if (
                !propertySet.isEnumerated
                && user?.hasPermissionOnPropertySet( 'read', propertySet.id, schema )
                && user?.hasGenericPermissionOnPropertySet( 'read', propertySet.id, schema )
            ) {
                retVal.push( propertySet );
            }
        }

        return retVal;
    }

    /**
     * Gets the PropertySets.
     *
     * @param props
     */
    static getInsertablePropertySets( props : AllProps ) : PropertySet[] {
        const retVal : PropertySet[] = [],
              {
                  schema,
                  itemTypeId,
                  properties,
                  user
              }                      = props,
              propertySets           = metaModel[ schema ].getPropertySetsForItemType( itemTypeId );

        for ( const propertySet of propertySets ) {
            if (
                !propertySet.isEnumerated
                && !properties.has( propertySet.propertyTypeId )
                && user?.hasPermissionOnPropertySet( 'insert', propertySet.id, schema )
                && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySet.id, schema )
            ) {
                retVal.push( propertySet );
            }
        }

        return retVal;
    }

    /**
     * Gets the data for DataTable.
     *
     * @param props
     * @param propertySets
     */
    static getData( props : AllProps, propertySets : PropertySet[], ) : RowData[] {
        const retVal : RowData[] = [],
              {
                  schema,
                  timezone,
                  properties,
                  rawMode,
                  showAdditionalRawNames,
                  showEmptyProperties,
                  showPropertyDefaults,
                  user
              }                  = props;

        for ( const propertySet of propertySets ) {
            const property = properties.get( propertySet.propertyTypeId );

            let value : string | JSX.Element | ( string | JSX.Element )[] | undefined,
                valueDefault : string | JSX.Element | ( string | JSX.Element )[] | undefined;

            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            if ( property || showEmptyProperties ) {

                if ( propertySet.valueSetId ) {
                    if ( property ) {
                        value = parse( getValueDisplayName( propertySet.valueSetId, convertValueToString( property.value, propertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                    if ( propertySet.valueDefault !== undefined && propertySet.valueDefault !== null && propertySet.valueDefault !== '' ) {
                        valueDefault = parse( getValueDisplayName( propertySet.valueSetId, convertValueToString( propertySet.valueDefault, propertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                }
                else {
                    const params = {
                        //filterString: '',
                        language : user?.language,
                        schema,
                        showAdditionalRawNames,
                        timezone,
                        valueUnit: propertySet.valueUnit,
                        valuePrecision,
                        editMode : true
                    };

                    if ( property ) {
                        value = renderValue( propertySet.valueType, property.value, params, rawMode );
                    }
                    if ( propertySet.valueDefault !== undefined && propertySet.valueDefault !== null && propertySet.valueDefault !== '' ) {
                        valueDefault = renderValue( propertySet.valueType, propertySet.valueDefault, params, rawMode );
                    }
                }

                retVal.push( {
                    propertySet     : propertySet,
                    propertyTypeName: getPropertySetDisplayName( propertySet.id, schema, rawMode, showAdditionalRawNames ),
                    valueType       : propertySet.valueSetId ? 'VALUESET' : propertySet.valueType,
                    valueDefault    : showPropertyDefaults ? valueDefault : undefined,
                    property        : property,
                    value           : value,
                    status          : property ? statusRenderer( property.statusId, schema, rawMode, showAdditionalRawNames ) : undefined,
                    modifierUserId  : property?.modifierUserId,
                    modificationTime: property?.modificationTime,
                    validatorUserId : property?.validatorUserId,
                    validationTime  : property?.validationTime,
                    validFromTime   : property?.validFromTime,
                    validToTime     : property?.validToTime
                } );
            }
        }

        return retVal;
    }

    /**
     * Sorts the data for the DataTable.
     *
     * @param data
     * @param multiSortMeta
     *
     * @private
     */
    static sortData( data : RowData[], multiSortMeta : DataTableSortMeta[] ) : RowData[] {
        const sortFnc = function( rowData1 : RowData, rowData2 : RowData, sortMeta : DataTableSortMeta[], index : number ) : number {
            const attribute = sortMeta[ index ].field as keyof RowData,
                  value1    = rowData1[ attribute ]!,
                  value2    = rowData2[ attribute ]!;

            let result = null;

            if ( rowData1.id === 0 ) {
                result = -sortMeta[ index ].order!;
            }
            else if ( rowData2.id === 0 ) {
                result = sortMeta[ index ].order!;
            }
            else if ( value1 !== value2 ) {
                result = value1 < value2 ? -1 : 1;
            }
            else {
                return sortMeta.length - 1 > index ? sortFnc( rowData1, rowData2, sortMeta, index + 1 ) : 0;
            }

            return sortMeta[ index ].order! * result;
        };

        if ( multiSortMeta?.length ) {
            data = data.sort( ( rowData1, rowData2 ) => {
                return sortFnc( rowData1, rowData2, multiSortMeta, 0 );
            } );
        }

        return data;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.setDataTableState();
        this.processDeepLink();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        this.processDeepLink();
    }


    /* event handlers *********************************************************************************************************************/

    /**
     * Handler for DataTable rowClass callback.
     *
     * @private
     */
    onResolveRowClass( rowData : RowData ) : { [ className : string ] : boolean } {
        const retVal : { [ className : string ] : boolean } = {},
              schema                                        = this.props.schema,
              property                                      = rowData.property;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        if ( property ) {
            // mark added row
            if ( property.id < 0 ) {
                retVal[ 'yn-added' ] = true;
            }

            // mark deleted row
            else if (
                property.isAttributeModified( 'statusId' )
                && (
                    property.statusId === historyStatusId[ schema ]
                    || property.statusId === deletedStatusId[ schema ]
                )
            ) {
                retVal[ 'yn-deleted' ] = true;
            }

            // mark modified row
            else if ( property.isModified() ) {
                retVal[ 'yn-modified' ] = true;
            }
        }

        return retVal;
    }

    /**
     * Handler for all editors beforeShow
     *
     * @private
     */
    onBeforeCellEditShow( params : ColumnEvent ) {
        const {
                  schema,
                  editable,
                  showEmptyProperties
              }       = this.props,
              field   = params.field,
              rowData = this.state.data[ params.rowIndex ],
              {
                  propertySet,
                  property
              }       = rowData;

        let retVal : boolean = false,
            value            = rowData.editValue ?? property?.value ?? propertySet?.valueDefault;

        // editable?
        /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
        if (
            editable
            && (
                (
                    propertySet
                    && field !== 'propertyTypeName'
                    && (
                        (
                            property
                            && this.isPropertySetUpdatable( propertySet.id )
                            && property.statusId !== historyStatusId[ schema ]
                            && property.statusId !== deletedStatusId[ schema ]
                        )
                        || (
                            !property
                            && this.isPropertySetInsertable( propertySet.id )
                        )
                    )
                )
                || (
                    !propertySet
                    && field === 'propertyTypeName'
                    && !showEmptyProperties
                )
            )
        ) {
            retVal = true;
        }
        /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

        if ( retVal ) {
            // setting boolean value directly and deny editing
            if ( field === 'value' && propertySet?.valueType === 'BOOLEAN' ) {
                value = property
                    ? !property.value
                    : propertySet.valueDefault === '' ? true : !propertySet.valueDefault;

                this.setPropertyAttributes( propertySet.propertyTypeId, { value } );

                retVal = false;
            }

            // deny editing for new property (unsaved) - allow only propertyTypeName
            else if ( !propertySet && field !== 'propertyTypeName' ) {
                retVal = false;
            }

            // deny editing status for new property (unsaved)
            else if ( !property && field === 'status' ) {
                retVal = false;
            }
        }

        return retVal;
    }

    /**
     * Handler for all editors cancel
     * Restore the initial value of editor init or delete just added property
     *
     * @private
     */
    onCellEditCancel( params : ColumnEvent ) {
        this.setState( { data: [] } );
    }

    /**
     * Handler for editors submit
     *
     * @private
     */
    onCellEditComplete( params : ColumnEvent ) {
        const {
                  schema,
                  timezone,
                  itemStatusId,
                  itemTypeId,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }                  = this.props,
              data               = ( [] as RowData[] ).concat( this.state.data ),
              field              = params.field,
              rowData            = data[ params.rowIndex ],
              {
                  propertySet,
                  property
              }                  = rowData,
              editValueAttribute = `edit${_.upperFirst( field )}` as keyof RowData,
              value              = rowData[ editValueAttribute ] as AttributeValue,
              error              = rowData.error,
              attributes         = {} as PropertyAttributesSettable;

        // handle changes
        if ( value !== undefined && value !== null && value !== '' && !error ) {

            // existing property
            if ( propertySet && property ) {
                if ( field === 'value' ) {
                    attributes[ 'value' ] = value;
                }
                else if ( field === 'status' ) {
                    attributes[ 'statusId' ] = metaModel[ this.props.schema ].getStatus( value as string )?.id
                }

                this.setPropertyAttributes( propertySet.propertyTypeId, attributes );
            }

            // new property (unsaved)
            else {
                if ( field === 'propertyTypeName' ) {
                    const newPropertySet = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( itemTypeId, value as string );

                    if ( newPropertySet && newPropertySet !== rowData.propertySet ) {
                        rowData.propertySet = newPropertySet;
                        rowData.propertyTypeName = getPropertySetDisplayName( newPropertySet.id, schema, rawMode, showAdditionalRawNames );
                    }
                }
                else if ( field === 'value' && propertySet ) {
                    rowData.editValue = value;

                    if ( propertySet.valueSetId ) {
                        rowData.value = parse( getValueDisplayName( propertySet.valueSetId, convertValueToString( value, propertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
                    }
                    else {
                        rowData.value = renderValue(
                            propertySet.valueType,
                            value,
                            {
                                //filterString: '',
                                language : user?.language,
                                showAdditionalRawNames,
                                timezone,
                                valueUnit: propertySet.valueUnit,
                                valuePrecision,
                                editMode : true
                            },
                            rawMode
                        );
                    }
                }

                // propertySet and value set -> create property
                if (
                    rowData.propertySet
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    && rowData.editValue !== undefined && rowData.editValue !== null && rowData.editValue !== ''
                ) {
                    this.setPropertyAttributes( rowData.propertySet.propertyTypeId, {
                        value   : rowData.editValue,
                        statusId: itemStatusId
                    } );

                    this.dataTable?.reset();
                    this.setState( { data: [] } );
                }

                // save edited value in rowData
                else {
                    this.setState( { data } );
                }
            }
        }

        // clear editValueAttribute and error in rowData
        else {
            this.setState( { data: [] } );
        }
    }

    /**
     * Handler for value editor resize
     *
     * @private
     */
    onValueEditorResize( width : number, height : number ) {
        const dataTableState = { ...this.props.dataTableState },
              columnWidths   = dataTableState?.columnWidths?.split( ',' );

        if ( columnWidths?.length ) {
            let valueColumnIndex = 2;

            if ( dataTableState?.columnOrder?.length ) {
                for ( let i = 0; i < dataTableState.columnOrder.length; i++ ) {
                    if ( dataTableState.columnOrder[ i ] === 'value' ) {
                        valueColumnIndex = i;
                        break;
                    }
                }
            }
            columnWidths[ valueColumnIndex ] = width;
            dataTableState.columnWidths = columnWidths.join( ',' )

            this.props.setParam( `${this.props.uid}`, 'dataTableState', dataTableState );
        }
    }

    /**
     * Handler for propertyType editor change
     *
     * @private
     */
    onPropertyTypeEditorChange( options : ColumnEditorOptions, event : DropdownChangeEvent ) {
        const {
                  schema,
                  timezone,
                  itemTypeId,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }              = this.props,
              value          = event.value,
              rowData        = this.state.data[ options.rowIndex ],
              newPropertySet = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( itemTypeId, value as string );

        rowData.editPropertyTypeName = value;

        if ( newPropertySet ) {
            rowData.valueType = newPropertySet.valueSetId ? 'VALUESET' : newPropertySet.valueType;
            if ( newPropertySet.valueSetId ) {
                rowData.value = parse( getValueDisplayName( newPropertySet.valueSetId, convertValueToString( newPropertySet.valueDefault, newPropertySet.valueType ), schema, rawMode, showAdditionalRawNames ) );
            }
            else {
                rowData.value = renderValue(
                    newPropertySet.valueType,
                    newPropertySet.valueDefault,
                    {
                        //filterString: '',
                        language : user?.language,
                        showAdditionalRawNames,
                        timezone,
                        valueUnit: newPropertySet.valueUnit,
                        valuePrecision,
                        editMode : true
                    },
                    rawMode
                );
            }
            rowData.editValue = newPropertySet.valueDefault;
        }

        options?.editorCallback?.( value );
    }

    /**
     * Handler for value editor change
     *
     * @private
     */
    onValueEditorChange( options : ColumnEditorOptions, value : AttributeValue | undefined, originalEvent : any ) {
        const rowData = this.state.data[ options.rowIndex ];

        rowData.editValue = value as AttributeValue;
        delete rowData.error;

        if ( value === undefined || value === null || value === '' ) {
            rowData.error = __( 'YN.base.finder.property.value.error.empty' );
        }
        else if ( rowData.propertySet ) {
            try {
                validateValueForType( value, rowData.propertySet.valueType, true );
            }
            catch ( e ) {
                if ( e instanceof Error ) {
                    rowData.error = e.toString();
                }
                else {
                    rowData.error = String( e );
                }
            }
        }

        options?.editorCallback?.( '' );
    }

    /**
     * Handler for status editor change
     *
     * @private
     */
    onStatusEditorChange( options : ColumnEditorOptions, status : Status ) {
        const rowData = this.state.data[ options.rowIndex ];

        rowData.editStatus = status;

        options?.editorCallback?.( status );
    }

    /**
     * Handler for add row button click.
     *
     * @private
     */
    onAddRowButtonClick() {
        const data = ( [] as RowData[] ).concat( this.state.data );

        data.unshift( { id: 0 } );

        this.setState( { data } );
    }

    /**
     * Handler for action column button click
     *
     * @private
     */
    onActionButtonClick( type : 'delete' | 'restore' | 'physicalDelete' | 'physicalRestore', rowData : RowData, event : MouseEvent<HTMLButtonElement> ) {
        const {
                  schema,
                  itemStatusId
              }        = this.props,
              property = rowData.property;

        let statusId : number;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        // existing property
        if ( property ) {
            switch ( type ) {
                case 'delete':
                    statusId = historyStatusId[ schema ]!;
                    break;

                case'physicalDelete':
                    statusId = deletedStatusId[ schema ]!;
                    break;

                case 'restore':
                case 'physicalRestore':
                    statusId = property.attributesModified.get( 'statusId' ) as number ?? itemStatusId;
                    break;
            }

            this.setPropertyAttributes( property.propertySet.propertyTypeId, { statusId } );
        }

        // new property (unsaved)
        else if ( type === 'delete' || type === 'physicalDelete' ) {
            const data = ( [] as RowData[] ).concat( this.state.data );

            data.splice( data.indexOf( rowData ), 1 );

            this.setState( { data } );
        }

        event.stopPropagation();
        event.preventDefault();
    }

    /**
     * Handler for DataTable onCustomSaveState event to extract the columns, filters and sorters
     *
     * @param {DataTableState} dataTableState
     *
     * @private
     */
    onCustomSaveState( dataTableState : DataTableState ) {
        if ( !this.ignoreDataTableSaveState && !shallowEqual( dataTableState, this.props.dataTableState ) ) {
            this.props.setParam( this.props.uid, 'dataTableState', dataTableState );
        }

        this.ignoreDataTableSaveState = false;
    }

    /**
     * Handler for DataTable onCustomRestoreState event to inject the columns, filters and sorters
     *
     * @return {DataTableState}
     *
     * @private
     */
    onCustomRestoreState() {
        return {
            ...this.props.dataTableState
        };
    }

    /**
     * Handler for dataTable contextMenuSelectionChange
     *
     * @private
     */
    onContextMenuSelectionChange( event : DataTableContextMenuSingleSelectionChangeEvent<RowData[]> ) {
        const rowData = event.value,
              field   = this.getFieldForMouseEvent( event.originalEvent as unknown as MouseEvent ) ?? 'n/a';

        this.setState( {
            contextMenuSelection: event.value,
            contextMenuCellInfo : {
                columnField: field,
                rowData
            }
        } );
    }

    /**
     * Handler for dataTable contextMenu
     *
     * @private
     */
    onContextMenu( event : DataTableRowEvent ) {
        this.contextMenu?.show( event.originalEvent );
    }

    /**
     * Handler for contextMenu hide
     *
     * @private
     */
    onContextMenuHide() {
        this.setState( {
            contextMenuSelection: undefined,
            contextMenuCellInfo : undefined
        } );
    }


    /* private ****************************************************************************************************************************/

    /**
     * Processes given deepLinkProps.
     *
     * @private
     */
    processDeepLink() {
        if ( this.props.deepLinkProps ) {
            const {
                      uid,
                      itemTypeId,
                      deepLinkProps
                  }             = this.props,
                  propertySetId = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( itemTypeId, deepLinkProps.property )?.id;

            const clearDeepLinkProps = true;

            if ( propertySetId ) {
                const {
                          data
                      }        = this.state,
                      rowIndex = data.findIndex( rowData => rowData.propertySet?.id === propertySetId );

                if ( rowIndex ) {
                    const dataTableRows = document.querySelectorAll( `#${uid}_properties .p-datatable-tbody tr` ) as NodeListOf<HTMLElement>,
                          row           = dataTableRows?.[ rowIndex ];

                    if ( row ) {
                        row.scrollIntoView( {
                            behavior: 'smooth',
                            block   : 'center',
                            inline  : 'nearest'
                        } );
                    }
                }
            }

            if ( clearDeepLinkProps ) {
                this.props.unsetParam( uid, 'deepLinkProps' );
            }
        }
    }

    /**
     * Sets the DataTable's state
     *
     * @private
     */
    setDataTableState() {
        const dataTableState = this.onCustomRestoreState();

        if ( !_.isEqual( this.lastDataTableState, dataTableState ) ) {
            this.lastDataTableState = dataTableState;

            this.dataTable?.reset();
            this.dataTable?.restoreTableState( dataTableState );
        }
    }

    /**
     * Builds the contextMenu content for current selected row.
     */
    getContextMenuModel( contextMenuCellInfo : State['contextMenuCellInfo'] ) : MenuItem[] {
        const retVal : MenuItem[] = [],
              {
                  editable
              }                   = this.props;

        if ( editable && contextMenuCellInfo ) {
            const property = contextMenuCellInfo.rowData.property;

            // revert
            if ( property?.isModified() && this.isPropertySetUpdatable( property.propertySetId ) ) {
                retVal.push(
                    {
                        //key    : 'revert',
                        label  : __( 'YN.base.frontend.dataTable.contextMenu.revert' ),
                        icon   : 'pi pi-fw pi-undo',
                        command: () => {
                            const attributes : any = {};

                            property.attributesModified.forEach( ( value, key ) => {
                                attributes[ key ] = value;
                            } );

                            this.setPropertyAttributes( property.propertySet.propertyTypeId, attributes );
                        }
                    }
                );
            }
        }

        return retVal;
    }

    /**
     * Gets the columnField for an mouse event if it is possible
     * @param event
     */
    getFieldForMouseEvent( event : MouseEvent ) : string | undefined {
        let retVal : string | undefined;

        const {
                  status,
                  timestamp,
                  showEmptyProperties,
                  editable,
                  user
              }             = this.props,
              {
                  insertablePropertySets
              }             = this.state,
              targetArea    = getTargetArea( status, timestamp ),
              columns       = this.getColumns( targetArea, showEmptyProperties, editable, insertablePropertySets, user?.language ),
              targetElement = event.target as unknown as Element;

        // get TD element
        let cellElement : Element | null       = targetElement.parentElement,
            cellNode : Node | null | undefined = targetElement.parentNode;
        while ( cellElement !== null && cellElement.tagName !== 'TD' ) {
            cellElement = cellElement.parentElement;
            cellNode = cellNode?.parentNode;
        }

        // get row
        const rowElement = cellElement?.parentElement;

        // get columnIndex
        let columnIndex : number | undefined;
        if ( rowElement ) {
            for ( let i = 0; i < rowElement.childNodes.length; i++ ) {
                if ( rowElement.childNodes[ i ] === cellNode ) {
                    columnIndex = i;
                    break;
                }
            }
        }

        // get field
        if ( columnIndex !== undefined ) {
            retVal = columns[ columnIndex ]?.props?.field;
        }

        return retVal;
    }

    /**
     * Empty handler method.
     *
     * @private
     */
    emptyFnc( event : MouseEvent<HTMLButtonElement> ) {
        event.stopPropagation();
        event.preventDefault();
    }

    isPropertySetReadable( propertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'read', propertySetId, schema )
            && user?.hasGenericPermissionOnPropertySet( 'read', propertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isPropertySetUpdatable( propertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'update', propertySetId, schema )
            && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isPropertySetInsertable( propertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'insert', propertySetId, schema )
            && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    isPropertySetDeletable( propertySetId : number ) : boolean {
        const {
                  schema,
                  user
              } = this.props;

        let retVal = false;

        if (
            user?.hasPermissionOnPropertySet( 'delete', propertySetId, schema )
            && user?.hasGenericPermissionOnPropertySet( 'nonBlocked', propertySetId, schema )
        ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * Sets the given attribute to given value for this items property given by propertyTypeId
     *
     * @param propertyTypeId
     * @param attributesToSet
     */
    setPropertyAttributes( propertyTypeId : number, attributesToSet : PropertyAttributesSettable ) {
        const {
                  dataContext,
                  itemId
              }                             = this.props,
              propertyTypeIdString          = String( propertyTypeId ),
              data : ItemAttributesSettable = {};

        data.properties = {};
        data.properties[ propertyTypeIdString ] = attributesToSet;

        this.props.updateItem( dataContext, itemId, data );
    }

    /**
     * Builds and returns the columns for properties DataTable.
     *
     * @param targetArea
     * @param showEmptyProperties
     * @param editable
     * @param insertablePropertySets
     * @param language
     *
     * @private
     */
    getColumns( targetArea : 'active' | 'all', showEmptyProperties : boolean, editable : boolean, insertablePropertySets : PropertySet[], language? : string ) : JSX.Element[] {
        const retVal : JSX.Element[] = [],
              columnDefaults         = {
                  sortable       : true,
                  body           : this.columnBodyRenderer,
                  bodyClassName  : 'yn-nowrap-top-column yn-nopadding-column',
                  headerClassName: 'yn-header-column'
              };

        if ( editable ) {
            let header : string | JSX.Element = '';

            if ( !showEmptyProperties && insertablePropertySets.length ) {
                header = <Button
                    key="addRowButton"

                    onClick={this.onAddRowButtonClick}

                    icon="pi pi-plus-circle"
                    className="yn-action-button yn-green-button"
                />;
            }
            else {
                header = <Button
                    key="addRowButton"

                    onClick={this.emptyFnc}

                    icon="pi pi-plus-circle"
                    className="yn-action-button yn-green-button"

                    disabled={true}
                    style={{ opacity: 0 }}
                />;
            }

            retVal.push( <Column
                key="deleteOrRestore"
                field="deleteOrRestore"
                header={header}

                filter={false}
                reorderable={false}
                sortable={false}
                resizeable={false}

                body={this.columnActionRenderer}
                bodyClassName={`${columnDefaults.bodyClassName} yn-action-column`}
                headerClassName="yn-header-action-column"
                filterHeaderClassName="yn-header-action-column"
            /> );
        }

        retVal.push( <Column
            key="propertyTypeName"
            field="propertyTypeName"
            header={__( 'YN.base.finder.detail.properties.propertyTypeName' )}
            {...columnDefaults}
            style={{ width: '15rem', minWidth: '10rem' }}

            editor={this.getPropertyTypeEditor}
            onBeforeCellEditShow={this.onBeforeCellEditShow}
            onCellEditCancel={this.onCellEditCancel}
            onCellEditComplete={this.onCellEditComplete}
        /> );

        retVal.push( <Column
            key="value"
            field="value"
            header={__( 'YN.base.finder.detail.properties.value' )}
            {...columnDefaults}
            bodyClassName={`${columnDefaults.bodyClassName} yn-column`}
            style={{ width: 'auto', minWidth: '15rem' }}

            editor={this.getValueEditor}
            onBeforeCellEditShow={this.onBeforeCellEditShow}
            onCellEditCancel={this.onCellEditCancel}
            onCellEditComplete={this.onCellEditComplete}
        /> );

        retVal.push( <Column
            key="valueType"
            field="valueType"
            header={__( 'YN.base.finder.detail.properties.valueType' )}
            {...columnDefaults}
            style={{ width: '8rem' }}
        /> );

        retVal.push( <Column
            key="status"
            field="status"
            header={__( 'YN.base.finder.status' )}
            {...columnDefaults}
            style={{ width: '5rem' }}

            editor={this.getStatusEditor}
            onBeforeCellEditShow={this.onBeforeCellEditShow}
            onCellEditCancel={this.onCellEditCancel}
            onCellEditComplete={this.onCellEditComplete}
        /> );

        // metaData

        // active
        if ( targetArea === 'active' ) {
            retVal.push( <Column
                key="modificationTime"
                field="modificationTime"
                header={__( 'YN.base.finder.modifiedAt' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );
            retVal.push( <Column
                key="modifierUserId"
                field="modifierUserId"
                header={__( 'YN.base.finder.modifiedBy' )}
                {...columnDefaults}
                style={{ width: '8rem' }}
            /> );

            retVal.push( <Column
                key="validationTime"
                field="validationTime"
                header={__( 'YN.base.finder.validatedAt' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );
            retVal.push( <Column
                key="validatorUserId"
                field="validatorUserId"
                header={__( 'YN.base.finder.validatedBy' )}
                {...columnDefaults}
                style={{ width: '8rem' }}
            /> );
        }

        // all
        else {
            retVal.push( <Column
                key="modifierUserId"
                field="modifierUserId"
                header={__( 'YN.base.finder.modifiedBy' )}
                {...columnDefaults}
                style={{ width: '8rem' }}
            /> );

            retVal.push( <Column
                key="validFromTime"
                field="validFromTime"
                header={__( 'YN.base.finder.validFrom' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );

            retVal.push( <Column
                key="validToTime"
                field="validToTime"
                header={__( 'YN.base.finder.validTo' )}
                {...columnDefaults}
                style={{ width: '10.2rem' }}
            /> );
        }

        return retVal;
    }

    /**
     * Column renderer
     *
     * @private
     */
    columnActionRenderer( rowData : RowData, columnProps : ColumnBodyOptions ) : string | JSX.Element | ( string | JSX.Element )[] | undefined {
        const {
                  schema
              } = this.props,
              {
                  id,
                  property
              } = rowData;

        let retVal : string | JSX.Element | ( string | JSX.Element )[] = '',
            icon,
            className,
            onClickHandler,
            enabled                                                    = false;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        const isHistory = property?.statusId === historyStatusId[ schema ],
              isDeleted = property?.statusId === deletedStatusId[ schema ];

        // restore
        if ( property && ( isHistory || isDeleted ) ) {
            icon = 'pi pi-plus-circle';
            className = 'yn-action-button yn-green-button';

            onClickHandler = this.onActionButtonClick.bind( this, 'physicalRestore', rowData );

            if (
                isHistory && this.isPropertySetUpdatable( property.propertySetId )
                || isDeleted && this.isPropertySetInsertable( property.propertySetId )
            ) {
                enabled = true;
            }
        }

            // delete
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        else if ( property || id === 0 ) {
            icon = 'pi pi-minus-circle';
            className = 'yn-action-button yn-red-button';

            onClickHandler = this.onActionButtonClick.bind( this, 'physicalDelete', rowData );

            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            if ( ( property && this.isPropertySetDeletable( property.propertySetId ) ) || id === 0 ) {
                enabled = true;
            }
        }

        if ( onClickHandler ) {
            retVal = <Button
                onClick={
                    enabled
                        ? onClickHandler
                        : this.emptyFnc
                }

                disabled={!enabled}

                icon={icon}
                className={`${className}`}
            />;
        }

        return retVal;
    }

    /**
     * Column renderer
     *
     * @private
     */
    columnBodyRenderer( rowData : RowData, columnProps : ColumnBodyOptions ) : string | JSX.Element | ( string | JSX.Element )[] | undefined {
        const {
                  schema,
                  timezone,
                  showPropertyDefaults,
                  rawMode
              } = this.props,
              {
                  propertySet,
                  property
              } = rowData,
              {
                  field
              } = columnProps;

        let retVal : string | JSX.Element | ( string | JSX.Element )[] = '',
            colValue                                                   = { ...rowData }[ field as keyof RowData ];

        switch ( field ) {
            case 'propertyTypeName':
                if ( !propertySet && !colValue ) {
                    colValue = '<edit to add new one>';
                }
                break;

            case 'value':
                if ( !property ) {
                    if ( rowData.editPropertyTypeName && !colValue ) {
                        colValue = '<edit to add new one>';
                    }
                    // get default for value
                    else if ( showPropertyDefaults && rowData.valueDefault !== undefined ) {
                        colValue = rowData.valueDefault;
                    }
                }
                break;

            case 'modificationTime':
            case 'validationTime':
            case 'validFromTime':
            case 'validToTime':
                if ( colValue ) {
                    colValue = timestampRenderer( ( colValue as string | Date | undefined ) ?? '', timezone, rawMode )
                }
                break;
        }

        if ( colValue ) {
            if ( !property && field === 'value' && !rowData.editPropertyTypeName ) {
                retVal = statusColoringRenderer( colValue, 0, undefined, undefined, undefined, 'yn-cell-renderer-el property-default' );
            }
            else if ( !property ) {
                // aria-controls is used for addressing the field in e2e tests
                retVal = <span className="yn-cell-renderer-el yn-empty" aria-controls={field === 'propertyTypeName' ? propertySet?.propertyType?.name ?? '' : undefined}>{colValue as string}</span>;
            }
            else if ( field === 'propertyTypeName' ) {
                retVal = statusColoringRenderer( <span aria-controls={propertySet?.propertyType?.name ?? ''}>{colValue as string}</span>, property.statusId, schema, undefined, undefined, 'yn-cell-renderer-el' );
            }
            else {
                retVal = statusColoringRenderer( colValue, property.statusId, schema, undefined, undefined, 'yn-cell-renderer-el' );
            }
        }

        return retVal;
    }

    /**
     * Builds the editor for propertyType column.
     *
     * @private
     */
    getPropertyTypeEditor( columnProps : ColumnEditorOptions & { props : { id : string } } ) : JSX.Element {
        const {
                  uid,
                  schema,
                  rawMode,
                  showAdditionalRawNames
              }       = this.props,
              {
                  insertablePropertySets
              }       = this.state,
              rowData = this.state.data[ columnProps.rowIndex ],
              {
                  propertySet
              }       = rowData,
              data    = insertablePropertySets.map( insertablePropertySet => {
                  return {
                      value: insertablePropertySet.name,
                      label: getPropertySetDisplayName( insertablePropertySet.id, schema, rawMode, showAdditionalRawNames )
                  };
              } ),
              value   = propertySet?.name ?? rowData.editPropertyTypeName ?? '';

        return <Dropdown
            key="cellEditor"
            inputId={`${uid}_propertyType`}
            value={value}
            onChange={this.onPropertyTypeEditorChange.bind( this, columnProps )}
            options={data}
            className="yn-cell-editor yn-inputgroup"
        />;
    }

    /**
     * Builds the editor for value column.
     *
     * @private
     */
    getValueEditor( columnProps : ColumnEditorOptions & { props : { id : string } } ) : string | JSX.Element | ( string | JSX.Element )[] | undefined {
        const {
                  schema,
                  timezone,
                  rawMode,
                  showAdditionalRawNames,
                  user
              }            = this.props,
              rowData      = this.state.data[ columnProps.rowIndex ],
              {
                  propertySet,
                  property,
                  error
              }            = rowData,
              table        = document.getElementById( columnProps.props.id ),
              rows         = table ? table.querySelectorAll( 'tbody > tr' ) : [],
              row          = rows[ columnProps.rowIndex ],
              height       = Math.max( row?.clientHeight ?? 0, 90 ),
              editorParams = {
                  key              : 'cellEditor',
                  id               : 'valueEditor',
                  appendToElementId: 'document',
                  autoComplete     : false,
                  autoFocus        : true,
                  classNames       : [ 'yn-cell-editor' ],
                  infoTextLayout   : 'hidden',
                  labelWidth       : 0,
                  onResize         : this.onValueEditorResize
              };

        let retVal : string | JSX.Element | ( string | JSX.Element )[] | undefined;

        if ( propertySet ) {
            let value = rowData.editValue ?? property?.value ?? propertySet?.valueDefault;

            // get ValueSet editor
            if ( propertySet.valueSetId ) {
                retVal = getValueSetEditor( schema, 'property', propertySet.id, value, this.onValueEditorChange.bind( this, columnProps ), rawMode, showAdditionalRawNames, editorParams );
            }

            // get ValueType editor
            else {
                retVal = [];

                if ( error ) {
                    editorParams.classNames.push( 'yn-error-border' );

                    retVal.push(
                        <Tooltip
                            key="editorTooltip"
                            target={`#${editorParams.id}`}
                            event="focus"
                            my="left top"
                            at="left bottom"
                            className="yn-tooltip"
                        >
                            <div key="error" className="yn-tooltip-error">
                                <div className="yn-tooltip-header">
                                    {__( 'YN.base.common.tooltip.error.title' )}
                                </div>
                                {parse( error )}
                            </div>
                        </Tooltip>
                    );
                }

                if ( !value && ( propertySet.valueType === 'DATE' || propertySet.valueType === 'TIME' || propertySet.valueType === 'TIMESTAMP' ) ) {
                    value = new Date();
                }

                const params = {
                          timezone,
                          showAdditionalRawNames,
                          valueUnit: propertySet.valueUnit,
                          valuePrecision,
                          language : user?.language,
                          height
                      },
                      editor = getValueEditor( propertySet.valueType, value, this.onValueEditorChange.bind( this, columnProps ), rawMode, params, editorParams );

                if ( editor ) {
                    retVal.unshift( editor );
                }
            }
        }

        // return body renderer result to avoid flickering
        if ( !retVal ) {
            retVal = this.columnBodyRenderer( rowData, columnProps );
        }

        return retVal;
    }

    /**
     * Builds the editor for status column.
     *
     * @private
     */
    getStatusEditor( columnProps : ColumnEditorOptions ) : JSX.Element {
        const {
                  uid,
                  rawMode,
                  showAdditionalRawNames
              }       = this.props,
              rowData = this.state.data[ columnProps.rowIndex ],
              {
                  property
              }       = rowData,
              value   = rowData.editStatus ?? property?.status.name as Status;

        return <StatusDropdown
            uid={`${uid}_property_status`}
            key="cellEditor"
            value={value}
            onChange={this.onStatusEditorChange.bind( this, columnProps )}
            mode="settable"
            autoFocus={true}
            rawMode={rawMode}
            showAdditionalRawNames={showAdditionalRawNames}
            className="yn-cell-editor"
        />;
    }

    /**
     * REDUX stuff
     */
    render() {
        const {
                  uid,
                  status,
                  timestamp,
                  showEmptyProperties,
                  editable,
                  dataTableState,
                  user
              }            = this.props,
              {
                  insertablePropertySets,
                  data
              }            = this.state,
              targetArea   = getTargetArea( status, timestamp ),
              columns      = this.getColumns( targetArea, showEmptyProperties, editable, insertablePropertySets, user?.language ),
              emptyMessage = insertablePropertySets.length ? __( 'YN.base.finder.noSetProperty.label' ) : __( 'YN.base.finder.noPropertySets.label' );

        // avoid updating dataTable state after render
        this.ignoreDataTableSaveState = true;

        return <div style={{ minHeight: '200px', position: 'relative' }} className="yn-flex-layout-parent">
            <ContextMenu
                ref={element => this.contextMenu = element}

                model={this.getContextMenuModel( this.state.contextMenuCellInfo )}
                onHide={this.onContextMenuHide}
                style={{ width: 'auto' }}
            />
            <DataTable
                ref={element => this.dataTable = element}

                key={`${uid}_properties`}
                id={`${uid}_properties`}

                value={data}
                emptyMessage={emptyMessage}

                rowClassName={this.onResolveRowClass}
                className="p-datatable-striped yn-finder yn-datatable-border-none-top"
                style={{ width: '100% !important', zIndex: 1 }}
                tableStyle={{ tableLayout: 'fixed', width: '100%' }}

                resizableColumns
                columnResizeMode="expand"

                scrollable
                scrollHeight="flex"

                removableSort
                sortMode="multiple"
                multiSortMeta={dataTableState.multiSortMeta}

                lazy

                // state
                stateStorage="custom"
                customSaveState={this.onCustomSaveState}
                customRestoreState={this.onCustomRestoreState}

                contextMenuSelection={this.state.contextMenuSelection}
                onContextMenuSelectionChange={this.onContextMenuSelectionChange}
                onContextMenu={this.onContextMenu}

                pt={{
                    wrapper: {
                        style: {
                            '--table-width'   : dataTableState?.tableWidth ?? '100%',
                            '--table-header-height': '2.2rem'
                        }
                    }
                }}
            >
                {columns}
            </DataTable>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( PropertiesDataTable );

